import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, AlertTriangle, CheckCircle, RefreshCw, Globe } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import VideoPlayer from '../ui/VideoPlayer';
import { API_BASE_URL } from '../../config/api';

// List of admin UIDs
const ADMIN_UIDS = [
  // Add your admin UIDs here
  "iMTsoOPrg6eR4rlJmaL1f0pA07Y2"
];

const AdminAnalysisDetail = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { id } = useParams();
  const { currentUser, getIdToken } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser || !ADMIN_UIDS.includes(currentUser.uid)) {
        // Redirect non-admin users
        navigate('/');
      }
    };
    
    checkAdmin();
  }, [currentUser, navigate]);

  useEffect(() => {
    // First check if we have analysis in sessionStorage
    const storedAnalysis = sessionStorage.getItem('adminViewAnalysis');
    if (storedAnalysis) {
      try {
        const parsedAnalysis = JSON.parse(storedAnalysis);
        if (parsedAnalysis.id === id) {
          setAnalysis(parsedAnalysis);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('Failed to parse stored analysis', e);
      }
    }
    
    // If not in session storage, fetch from API
    fetchAnalysis();
  }, [id]);

  const handleRerunAnalysis = async () => {
    console.log('=== AdminAnalysisDetail handleRerunAnalysis ===');
    console.log('Current analysis:', analysis);

    // Immediately update local state to show processing
    const processingAnalysis = {
      ...analysis,
      processed: false,
      status: 'processing'
    };

    console.log('Setting local state to processing...');
    setAnalysis(processingAnalysis);

    // Update session storage with processing status
    sessionStorage.setItem('adminViewAnalysis', JSON.stringify(processingAnalysis));

    try {
      // Get authentication token
      const token = await getIdToken();

      if (!token) {
        throw new Error('Authentication required');
      }

      console.log('Calling rerun API...');
      const response = await fetch(`${API_BASE_URL}/analyses/${id}/rerun`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const responseData = await response.json();
      console.log('Rerun API response:', responseData);

      if (!response.ok) {
        throw new Error(responseData.error || 'Failed to rerun analysis');
      }

      // Show success message
      alert('Analysis rerun started successfully. This may take several minutes to complete.');

      // Navigate back to admin list after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 500);
    } catch (error) {
      console.error('Error rerunning analysis:', error);
      setError('Failed to rerun analysis: ' + error.message);
      // Revert to original state on error
      setAnalysis(analysis);
      sessionStorage.setItem('adminViewAnalysis', JSON.stringify(analysis));
    }
  };

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      
      // Get authentication token
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(`${API_BASE_URL}/admin/analyses/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAnalysis(data);
      setError(null);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      setError('Failed to load analysis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnalysis = async () => {
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
      return;
    }
    
    try {
      const token = await getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/analyses/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Navigate back to admin page
      navigate('/admin');
    } catch (error) {
      console.error('Error deleting analysis:', error);
      setError('Failed to delete analysis: ' + error.message);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const token = await getIdToken();
      
      // Create a link to download the PDF
      const link = document.createElement('a');
      link.href = `${API_BASE_URL}/analyses/${id}/pdf`;
      
      // Add token to the URL as a query parameter
      link.href += `?token=${encodeURIComponent(token)}`;
      
      link.download = `analysis_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF: ' + error.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    try {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(8, 10);
      const minute = timestamp.substring(10, 12);
      return `${year}/${month}/${day} ${hour}:${minute}`;
    } catch (e) {
      return timestamp;
    }
  };

  const formatDancers = (dancers, analysisType) => {
    if (!dancers) return 'Not specified';
    
    if (analysisType === 'solo') {
      return dancers.includes('Dancer:') 
        ? dancers 
        : `Dancer: ${dancers.replace(/Man: |Lady: |Unknown, /g, '')}`;
    } else if (analysisType === 'duo') {
      return dancers.includes('Duo:') 
        ? dancers 
        : `Duo: ${dancers.replace(/Man: |Lady: |Dancer: |Unknown, /g, '')}`;
    } else if (analysisType === 'formation') {
      return dancers.includes('Formation:') 
        ? dancers 
        : `Formation: ${dancers.replace(/Man: |Lady: |Dancer: |Duo: |Unknown, /g, '')}`;
    } else {
      // Default couple format
      return dancers;
    }
  };

  const formatFeedback = (text) => {
    if (!text) return 'No feedback available';
    
    // Split the text into sections
    const sections = text.split(/\*\*(.*?)\*\*/);
    const formattedSections = [];
    
    for (let i = 0; i < sections.length; i++) {
      if (i % 2 === 1) { // This is a header
        formattedSections.push(`<h2 class="text-xl font-bold text-blue-800 mt-6 mb-4">${sections[i]}</h2>`);
      } else { // This is content
        const formattedContent = sections[i]
          .replace(/^- (.+)$/gm, '<li class="mb-2">$1</li>')
          .replace(/\n\n/g, '</ul><ul class="list-disc pl-6 mb-4">');
        
        if (formattedContent.trim()) {
          formattedSections.push(`<ul class="list-disc pl-6 mb-4">${formattedContent}</ul>`);
        }
      }
    }
    
    return formattedSections.join('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading analysis...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          <h2 className="text-lg font-semibold mb-2">Error</h2>
          <p>{error}</p>
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Admin Dashboard
        </button>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="h-full">
        <div className="bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded mb-6">
          <h2 className="text-lg font-semibold mb-2">Analysis Not Found</h2>
          <p>The requested analysis could not be found.</p>
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Admin Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Admin Dashboard
        </button>

        <div className="flex gap-2">
          <button
            onClick={handleRerunAnalysis}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rerun Analysis
          </button>
          
          <button
            onClick={handleDownloadPDF}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
          >
            <Download className="w-4 h-4 mr-2" />
            Download PDF
          </button>
          
          <button
            onClick={handleDeleteAnalysis}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
        {/* Header */}
        <div className="bg-blue-800 text-white p-6">
          <div className="flex justify-between items-start">
            <h1 className="text-2xl font-bold mb-2">
              {analysis.dance_type || 'Dance Analysis'}
            </h1>
            <div className="flex items-center">
              {analysis.processed ? (
                <span className="flex items-center bg-green-500 text-white px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Processed
                </span>
              ) : (
                <span className="flex items-center bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  In Progress
                </span>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-4">
            <div>
              <p className="opacity-75 mb-1">Date:</p>
              <p className="font-medium">{formatDate(analysis.timestamp)}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Dancers:</p>
              <p className="font-medium">{formatDancers(analysis.dancers, analysis.analysis_type)}</p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">Analysis Type:</p>
              <p className="font-medium capitalize">
                {analysis.analysis_type === 'solo' 
                  ? 'Solo Analysis' 
                  : analysis.analysis_type === 'duo'
                    ? 'Duo Analysis'
                    : analysis.analysis_type === 'formation'
                      ? 'Formation Analysis'
                      : 'Couple Analysis'}
              </p>
            </div>
            
            <div>
              <p className="opacity-75 mb-1">User:</p>
              <p className="font-medium">{analysis.user_email || 'Unknown'}</p>
            </div>
          </div>
        </div>

        {/* Admin Info Section */}
        <div className="border-b bg-gray-50 p-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">Admin Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">Analysis ID:</p>
              <p className="font-mono bg-gray-100 p-1 rounded">{analysis.id}</p>
            </div>
            
            <div>
              <p className="text-gray-500 mb-1">User ID:</p>
              <p className="font-mono bg-gray-100 p-1 rounded">{analysis.user_id || 'N/A'}</p>
            </div>
            
            <div>
              <p className="text-gray-500 mb-1">User Display Name:</p>
              <p>{analysis.user_displayName || 'Unknown'}</p>
            </div>
            
            <div>
              <p className="text-gray-500 mb-1">Processing Status:</p>
              <p>{analysis.status || (analysis.processed ? 'Completed' : 'In Progress')}</p>
            </div>
            
            <div>
              <p className="text-gray-500 mb-1">Country:</p>
              <p className="flex items-center">
                <Globe className="w-4 h-4 mr-1 text-blue-500" />
                {analysis.user_country || 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* Video Section */}
        {analysis.video_url && (
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">Performance Video</h2>
            <div className="aspect-video">
              <VideoPlayer 
                videoUrl={analysis.video_url} 
                className="w-full h-full rounded-lg shadow"
              />
            </div>
          </div>
        )}

        {/* Analysis Content */}
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Analysis</h2>
          
          {analysis.processed ? (
            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{
                __html: analysis.text || analysis.analysis || 'No analysis content available.',
              }}
            />
          ) : (
            <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
              <span>Analysis in progress...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalysisDetail;